import {
  CO_REBATE_BASE,
  CO_REBATE_CARGO_BONUS,
  CO_TAX_CREDIT,
} from "@/lib/constants";

const usd = (n: number) => "$" + n.toLocaleString("en-US");

/**
 * `/faq`.
 *
 * These answers were fact-checked in a 2026-05 pass that stripped every
 * unverified specific — assembly fees, trade-in ranges, monthly payment
 * examples, a certification list, a service-brand list — and replaced them with
 * either industry-true statements or an honest "ask in store". **Do not put
 * numbers back without Ryan.** That pass is tracked in
 * `docs/migration/RYAN-FACT-CHECK-ASK.md`.
 *
 * The test-ride answers no longer claim this page itself reserves or
 * guarantees a bike. `/book` now carries the live Cal.com scheduler; these
 * answers recommend it for the concrete bike-ready benefit and keep walk-ins
 * available without making a site-level confirmation promise.
 */
export const faq = {
  hero: {
    line1: "Answers for choosing",
    line2: "a family cargo bike",
    intro:
      "Start with cost, bike type, test rides, Colorado savings, and service. If your question is not here, call or send it to the shop.",
  },

  categories: [
    {
      id: "buying",
      name: "Buying a cargo bike",
      questions: [
        {
          q: "How much does a cargo bike cost?",
          a: "Prices vary by model and setup. The bikes page shows Shopify's listed price when available. Call or text to confirm current pricing and availability. Colorado's $225 point-of-sale credit, an income-qualified rebate, a trade-in, and financing may reduce the amount due or spread it over time.",
        },
        {
          q: "Can I test ride before buying?",
          // Cal.com confirms its own slots; this answer only promises the
          // concrete shop benefit and keeps the walk-in path visible.
          a: "Yes. Test rides are free for 30 minutes. Schedule ahead and tell us what you want to compare so the bikes can be ready. You may also visit during open hours.",
        },
        {
          q: "What brands do you carry?",
          a: "Tern, Urban Arrow, Xtracycle, Benno, Riese & Müller, Flyer, and Gazelle. Check the bikes page for the models currently listed, then call or text to confirm what's on the floor.",
        },
        {
          q: "What's the difference between a front-loader, longtail, and compact cargo bike?",
          a: "A front-loader puts the load in a box between you and the front wheel, where you can see it. A longtail carries passengers or cargo behind the saddle and rides more like a regular bike. A compact cargo bike uses less storage space and carries less. Which one is right depends on what you're carrying and where you park it.",
        },
        {
          q: "Why should I buy from a local dealer instead of ordering online?",
          a: "You can compare bikes in one visit, ride before you decide, get help choosing the right setup, and return to the same shop for service after the sale.",
        },
      ],
    },
    {
      id: "money",
      name: "Financing & savings",
      questions: [
        {
          q: "How do I finance a cargo bike?",
          a: "Clean Energy Credit Union makes fixed-rate e-bike loans. Loan terms change with the amount borrowed, so check the lender's site for current numbers. Financing can still be used alongside Colorado's point-of-sale credit and a trade-in.",
        },
        {
          q: "What is the Colorado e-bike tax credit?",
          // SOURCE: https://energyoffice.colorado.gov/ebike-tax-credit
          // (2026-08-06) — "$225 discount per e-bike (limited to one discount a
          // year per resident)". An earlier draft said "every 24 months", which
          // was wrong.
          a: `Colorado residents get ${usd(CO_TAX_CREDIT)} off most ebikes — no income test, no separate application. Bring proof of a Colorado address and we handle the paperwork at the counter so the discount comes off your total. One discount per resident per calendar year. Couples can each use one.`,
        },
        {
          q: "What is the Colorado e-bike rebate, and is it different?",
          // SOURCE: https://energyoffice.colorado.gov/ebike-rebates (2026-08-06)
          a: `Different program, much bigger. It's ${usd(CO_REBATE_BASE)} for income-qualified Coloradans, plus ${usd(CO_REBATE_CARGO_BONUS)} more on a qualifying cargo bike, and it stacks on top of the ${usd(CO_TAX_CREDIT)} discount. You register during a state window and get picked at random — it's a lottery, not first-come. We're on the state's participating-retailer list for both programs. The savings page has the current registration dates.`,
        },
        {
          q: "Can I trade in my old e-bike?",
          a: "Use the contact form to share a photo and notes about the bike's condition, or visit with the bike. The shop quotes a value only after seeing it.",
        },
        {
          q: "How much can I save in total?",
          a: `The ${usd(CO_TAX_CREDIT)} Colorado credit can combine with an eligible rebate and a trade-in. Financing changes when you pay, not the purchase price. Your total depends on eligibility, the bike, and the trade-in value; the savings page separates each option.`,
        },
      ],
    },
    {
      id: "service",
      name: "Service & repair",
      questions: [
        {
          q: "Which cargo bikes can I bring in for service?",
          a: "Cargo bikes bought at Front Range Cargo Bikes. For a bike from another shop, call or text first and ask whether the bench can take it.",
        },
        {
          q: "How often should I service my cargo bike?",
          a: "Most riders bring a cargo bike in about once a year. Come in sooner if the brakes, shifting, tires, or motor start behaving differently.",
        },
        {
          q: "Do you offer loaner bikes during service?",
          a: "Call before you count on one. We don't promise loaner availability online.",
        },
        {
          q: "What does a tune-up include?",
          a: "A safety inspection is $45. Tunes start at $180 and go up with what the bike needs. We look first and tell you what the work will cost before it starts.",
        },
      ],
    },
    {
      id: "test-rides",
      name: "Test rides",
      questions: [
        {
          q: "How long is a test ride?",
          // Route confirmed with Ryan, 2026-05-19.
          a: "About 30 minutes. The route connects with the Goose Creek, Boulder Creek, and Foothills paths, giving you time to compare the motor, handling, starting, and stopping away from a parking lot.",
        },
        {
          q: "Do I need to arrange it in advance?",
          // Scheduling is recommended for a concrete shop-preparation benefit;
          // it does not claim this FAQ confirms or guarantees inventory.
          a: "You can stop in during open hours. Scheduling ahead lets the shop prepare the model you want to try; some bikes are stored off-site.",
        },
        {
          q: "Can my kids ride along on the test ride?",
          a: "Yes. Bring the children who will ride with you and helmets for everyone riding. Testing the passenger setup and handling together helps you compare bikes accurately.",
        },
        {
          q: "What should I bring to a test ride?",
          a: "Wear clothes you can pedal in. If you're testing a family cargo bike, bring the kids and their helmets. We'll handle the bike setup and show you the route.",
        },
      ],
    },
  ],

  stillStuckHeading: "Need an answer for your family or route?",
  stillStuckBody:
    "Call during shop hours for a quick answer, or send a message with what you carry, where you ride, and the bikes you are considering.",
} as const;
